import {formatCurrency, formatDate, formatNumber} from '@angular/common';
import {AdminDashboardKpi} from '@features/admin-panel/models/admin-dashboard.models';

/** What a KPI with no meaning in the scope says instead of a value (e.g. ARR for non-clients). */
export const KPI_NOT_APPLICABLE_TEXT = 'No Client Manager record';

/** What the delta line says when there is nothing to compare against (OCA-E-159 KpiStrip.tsx:52). */
export const KPI_NO_COMPARISON_TEXT = 'No comparison for this period';

/** Whether a KPI's change is good, bad or neither: the colour of its delta line and of its sparkline. */
export type KpiTone = 'good' | 'bad' | 'neutral';

/**
 * Which direction of change is good, by KPI key (OCA-E-159 metrics.ts `good`): less revenue and fewer accounts at
 * risk is good; renewals due and XatBot spend are neither good nor bad. Every other KPI is better when it goes up.
 */
export const KPI_GOOD_DIRECTION: {[key: string]: 'up' | 'down' | 'none'} = {
  'revenue-at-risk': 'down',
  'accounts-at-risk': 'down',
  'renewals-due': 'none',
  'xatbot-spend': 'none'
};

/** The comparison the delta line names, by dashboard period key. */
const KPI_COMPARE_TEXT: {[period: string]: string} = {
  '7d': 'vs previous 7 days',
  '30d': 'vs previous 30 days',
  '90d': 'vs previous 90 days',
  '12m': 'vs previous 12 months'
};

/** The KPI's value in its unit: "$12,500", "25%", "865"; "—" when there is no data. */
export function kpiValueText(kpi: AdminDashboardKpi): string {
  if (kpi?.value == null) {
    return '—';
  }
  switch (kpi.unit) {
    case 'usd':
      return formatCurrency(kpi.value, 'en-US', '$', 'USD', '1.0-0');
    case 'percent':
      return kpi.value + '%';
    default:
      return formatNumber(kpi.value, 'en-US');
  }
}

/** "of 887" when the KPI has a total, else null. */
export function kpiTotalText(kpi: AdminDashboardKpi): string | null {
  return kpi?.total != null ? `of ${formatNumber(kpi.total, 'en-US')}` : null;
}

/**
 * The unit beside the value (OCA-E-159 `.adb-kpi__unit`): the KPI's secondary text ("2 accounts", "1 of 4 trials",
 * "month to date", "of 12 companies") unless it is a comparison ("vs previous 30 days", which the delta line names
 * instead), else "of 887" from the total, else null.
 */
export function kpiUnitText(kpi: AdminDashboardKpi): string | null {
  const secondary = (kpi?.secondaryText ?? '').trim();
  return secondary && !secondary.startsWith('vs ') ? secondary : kpiTotalText(kpi);
}

/**
 * "as of 7 Oct" when the KPI's value describes a day before today (UTC) — the at-risk KPIs, which end at the latest
 * insight day (ITP-E-211-03, Plan ITP-P-50 Q3/Q12); null for a live KPI (no asOf), an unreadable asOf, or today.
 */
export function kpiAsOfText(kpi: AdminDashboardKpi, now: Date = new Date()): string | null {
  const asOf = kpi?.asOf ? Date.parse(kpi.asOf) : NaN;
  if (!Number.isFinite(asOf)) {
    return null;
  }
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return asOf < todayUtc ? `as of ${formatDate(kpi.asOf, 'd MMM', 'en-US', 'UTC')}` : null;
}

/** True when there is both a value and a previous value to compare. */
export function kpiHasComparison(kpi: AdminDashboardKpi): boolean {
  return kpi?.value != null && kpi?.previousValue != null;
}

/** Percentage change against the previous period, rounded to 1 decimal; null when the previous value is 0. */
export function kpiDelta(kpi: AdminDashboardKpi): number | null {
  if (!kpiHasComparison(kpi) || kpi.previousValue === 0) {
    return null;
  }
  return Math.round((kpi.value - kpi.previousValue) / kpi.previousValue * 1000) / 10;
}

/** "vs previous 30 days" for a dashboard period key ('7d' | '30d' | '90d' | '12m'); null for any other value. */
export function kpiCompareText(period: string | null | undefined): string | null {
  return period ? KPI_COMPARE_TEXT[period] ?? null : null;
}

/**
 * The delta line (OCA-E-159 metrics.ts pctDelta): "▲ 3.2% vs previous 30 days", "▼ 3.2% vs previous 30 days",
 * "No change vs previous 30 days" when it did not move (also from 0 to 0), and "No comparison for this period" without
 * a previous value or when the previous value is 0 and the value is not. Without a known period the "vs …" part is
 * left out ("▲ 3.2%", "No change"): the PDF report names the period in its header.
 */
export function kpiDeltaText(kpi: AdminDashboardKpi, period?: string | null): string {
  if (!kpiHasComparison(kpi) || (kpi.previousValue === 0 && kpi.value !== 0)) {
    return KPI_NO_COMPARISON_TEXT;
  }
  const compare = kpiCompareText(period);
  const suffix = compare ? ` ${compare}` : '';
  const delta = kpiDelta(kpi) ?? 0;
  if (delta === 0) {
    return `No change${suffix}`;
  }
  return `${delta > 0 ? '▲' : '▼'} ${Math.abs(delta)}%${suffix}`;
}

/**
 * Whether the change is good or bad for this KPI (OCA-E-159 KpiStrip.tsx toneOf): neutral without a change, while
 * history is still being collected, for a KPI that does not apply, and for a KPI whose change is neither
 * (KPI_GOOD_DIRECTION 'none'); otherwise good when it moved the good way.
 */
export function kpiTone(kpi: AdminDashboardKpi): KpiTone {
  const delta = kpiDelta(kpi);
  if (!delta || kpi.historyFrom || kpi.notApplicable) {
    return 'neutral';
  }
  const good = KPI_GOOD_DIRECTION[kpi.key] ?? 'up';
  if (good === 'none') {
    return 'neutral';
  }
  return (delta > 0) === (good === 'up') ? 'good' : 'bad';
}

/** "Collecting history — trend available from 20 Sep 2026" while the KPI's history does not cover the window, else null. */
export function kpiCollectingText(kpi: AdminDashboardKpi): string | null {
  return kpi?.historyFrom
    ? `Collecting history — trend available from ${formatDate(kpi.historyFrom, 'd MMM y', 'en-US', 'UTC')}`
    : null;
}

/**
 * The KPI tile as text, for the PDF report (ITP-E-146, ITP-E-149-01): the value, and the detail line — the unit
 * (kpiUnitText), then the collecting-history note or, when there is a comparison, the change without the period
 * ("2 accounts · ▲ 50%"), then "as of 7 Oct" for a KPI that describes an earlier day (ITP-E-211-03), joined by " · ".
 * A KPI with no meaning in the scope has no value ("—") and says "No Client Manager record".
 */
export function formatKpiTile(kpi: AdminDashboardKpi, period?: string | null, now: Date = new Date()): {value: string; detail: string | null} {
  if (kpi?.notApplicable) {
    return {value: '—', detail: KPI_NOT_APPLICABLE_TEXT};
  }
  const change = kpiCollectingText(kpi) ?? (kpiHasComparison(kpi) ? kpiDeltaText(kpi, period) : null);
  const parts = [kpiUnitText(kpi), change, kpiAsOfText(kpi, now)].filter(part => !!part);
  return {value: kpiValueText(kpi), detail: parts.length ? parts.join(' · ') : null};
}
